import { decodeRow } from './decoder.ts';
import { rasterizeText, type PixelFontName } from './pixelfont.ts';
import { COLS, rowFromString, rowToString, type Row } from './types.ts';

/**
 * Block art: a grid of cells, each with one foreground colour and six
 * sixel bits (TL, TR, ML, MR, BL, BR = bits 0–5), plus optional text blocks
 * drawn in a pixel font that stay editable until they are baked in. This
 * is what the CMS paint editor edits. Exporting to Level 1 rows applies
 * the real constraints: every colour change on a row needs a mosaic-colour
 * attribute cell. The exporter tries plain and hold-mosaics encodings per
 * row and keeps whichever displays closest to the intended picture.
 */
export interface ArtCell {
  /** 0–7, or -1 for an empty cell. */
  colour: number;
  /** Six sixel bits, TL TR ML MR BL BR. */
  bits: number;
}

export interface Art {
  width: number;
  height: number;
  /** Row-major, width × height. */
  cells: ArtCell[];
}

/** A text block positioned in sixels; `cutout` clears pixels instead of painting them. */
export interface TextBlock {
  id: string;
  text: string;
  /** Sixel coordinates of the top-left corner. */
  x: number;
  y: number;
  font: PixelFontName;
  colour: number;
  cutout: boolean;
}

/** The editable source of an asset: pixels plus live text blocks. */
export interface ArtSource {
  art: Art;
  texts: TextBlock[];
}

export interface ArtExport {
  rows: string[];
  /** Cells whose displayed pixels differ from the intended picture: [row, col]. */
  sacrificed: Array<[number, number]>;
}

export const EMPTY_CELL: ArtCell = { colour: -1, bits: 0 };

export function newArt(width: number, height: number): Art {
  return { width, height, cells: Array.from({ length: width * height }, () => ({ ...EMPTY_CELL })) };
}

export function cloneArt(art: Art): Art {
  return { width: art.width, height: art.height, cells: art.cells.map((c) => ({ ...c })) };
}

/** Sixel bits (0–5) → G1 mosaic code (bit 5 lives at 0x40). */
export function bitsToCode(bits: number): number {
  return 0x20 | (bits & 0x1f) | (bits & 0x20 ? 0x40 : 0);
}

/** G1 mosaic code → sixel bits (0–5). */
export function codeToBits(code: number): number {
  return (code & 0x1f) | (code & 0x40 ? 0x20 : 0);
}

/** Set or clear one sixel; painting recolours the whole cell (one colour per cell). */
export function setSixel(art: Art, sx: number, sy: number, colour: number, on: boolean): void {
  const cx = Math.floor(sx / 2), cy = Math.floor(sy / 3);
  if (cx < 0 || cy < 0 || cx >= art.width || cy >= art.height) return;
  const bit = 1 << ((sy % 3) * 2 + (sx % 2));
  const cell = art.cells[cy * art.width + cx]!;
  if (on) {
    cell.colour = colour;
    cell.bits |= bit;
  } else {
    cell.bits &= ~bit;
    if (cell.bits === 0) cell.colour = -1;
  }
}

/** Fill every cell with solid blocks of one colour. */
export function fillArt(art: Art, colour: number): void {
  for (const c of art.cells) { c.colour = colour; c.bits = 0x3f; }
}

/** Pixels + text blocks → the flat picture the exporter sees. */
export function composeArt(source: ArtSource): Art {
  const art = cloneArt(source.art);
  for (const t of source.texts) {
    const r = rasterizeText(t.text, t.font);
    for (let y = 0; y < r.height; y++) {
      for (let x = 0; x < r.width; x++) {
        if (!r.on[y * r.width + x]) continue;
        if (t.cutout) setSixel(art, t.x + x, t.y + y, -1, false);
        else setSixel(art, t.x + x, t.y + y, t.colour, true);
      }
    }
  }
  return art;
}

/** Merge a text block into the pixels. */
export function bakeText(source: ArtSource, id: string): ArtSource {
  const block = source.texts.find((t) => t.id === id);
  if (!block) return source;
  const art = composeArt({ art: source.art, texts: [block] });
  return { art, texts: source.texts.filter((t) => t.id !== id) };
}

const CODE_HOLD = 0x1e;

/** What the decoder shows for a row, as (colour, bits) per cell; attribute cells show what they display. */
function displayed(row: Row): Array<{ colour: number; bits: number }> {
  return decodeRow(row).map((c) => (c.mosaic && c.glyph >= 0x20 && codeToBits(c.glyph) !== 0 ? { colour: c.fg, bits: codeToBits(c.glyph) } : { colour: -1, bits: 0 }));
}

function intended(art: Art, y: number): Array<{ colour: number; bits: number }> {
  const out: Array<{ colour: number; bits: number }> = [];
  for (let x = 0; x < COLS; x++) {
    const c = x < art.width ? art.cells[y * art.width + x] ?? EMPTY_CELL : EMPTY_CELL;
    out.push(c.colour >= 0 && c.bits !== 0 ? { colour: c.colour, bits: c.bits } : { colour: -1, bits: 0 });
  }
  return out;
}

/**
 * Encode one row. Without hold, a colour change needs an attribute in the
 * preceding blank cell, or it sacrifices the changing cell. With hold
 * mosaics (one blank cell at column 1), an attribute cell displays the
 * previous mosaic character, so changes inside solid areas cost nothing
 * visible.
 */
function encodeRow(art: Art, y: number, hold: boolean): Row {
  const row: Row = new Array<number>(COLS).fill(0x20);
  let current = -1;
  let attrAt = -1;
  let x = 0;
  if (hold) { row[1] = CODE_HOLD; attrAt = 1; }
  for (x = 0; x < Math.min(art.width, COLS); x++) {
    if (hold && x <= 1) continue;
    const cell = art.cells[y * art.width + x] ?? EMPTY_CELL;
    const painted = cell.colour >= 0 && cell.bits !== 0;
    if (!painted) continue;
    if (cell.colour !== current) {
      const attr = 0x10 + cell.colour;
      if (current === -1 && x >= 1 && row[0] === 0x20) {
        row[0] = attr; // first colour of the row goes in column 0
      } else if (x > 0 && row[x - 1] === 0x20 && attrAt !== x - 1 && !hold) {
        row[x - 1] = attr;
        attrAt = x - 1;
      } else if (hold && x > 0 && (row[x - 1] ?? 0) >= 0x20 && attrAt !== x - 1 && x - 1 > 1) {
        row[x - 1] = attr; // held mosaic keeps the previous cell's look
        attrAt = x - 1;
      } else {
        row[x] = attr;
        attrAt = x;
        current = cell.colour;
        continue;
      }
      current = cell.colour;
    }
    row[x] = bitsToCode(cell.bits);
  }
  return row;
}

export function artToRows(art: Art): ArtExport {
  const rows: string[] = [];
  const sacrificed: Array<[number, number]> = [];
  for (let y = 0; y < art.height; y++) {
    const want = intended(art, y);
    let best: { row: Row; bad: number[] } | null = null;
    for (const hold of [false, true]) {
      const row = encodeRow(art, y, hold);
      const shown = displayed(row);
      const bad: number[] = [];
      for (let x = 0; x < COLS; x++) {
        const a = want[x]!, b = shown[x]!;
        if (a.bits !== b.bits || (a.bits !== 0 && a.colour !== b.colour)) bad.push(x);
      }
      if (!best || bad.length < best.bad.length) best = { row, bad };
    }
    rows.push(rowToString(best!.row));
    for (const x of best!.bad) sacrificed.push([y, x]);
  }
  return { rows, sacrificed };
}

/** Decode Level 1 rows back into an editable grid (attribute cells become what they display). */
export function rowsToArt(rows: string[], width = COLS): Art {
  const art = newArt(width, rows.length);
  rows.forEach((s, y) => {
    const shown = displayed(rowFromString(s));
    for (let x = 0; x < width; x++) {
      const c = shown[x]!;
      art.cells[y * width + x] = c.bits ? { colour: c.colour, bits: c.bits } : { ...EMPTY_CELL };
    }
  });
  return art;
}
